import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row, UncontrolledTooltip } from 'reactstrap';

import CircularLoading from '../../Common/CircularLoading/CircularLoading';

const RATE_SCOPES = [
  { value: 'pfe', label: 'PFE only' },
  { value: 'skt', label: 'SKT only' },
  { value: 'both', label: 'Both' },
];

function CustomSyncView({
  customSync,
  customSyncMessage,
  isLoadingCustomSync,
  isLoadingCustomSyncData,
  getCustomSync,
  updateCustomSync,
  setCustomSyncMessage,
  user,
}) {
  const [rateScope, setRateScope] = useState('both');
  const [favoriteQuestions, setFavoriteQuestions] = useState(true);
  const [onlyPersonalizedStudyChapters, setOnlyPersonalizedStudyChapters] =
    useState(false);
  const [onlyQuestionsWithoutImages, setOnlyQuestionsWithoutImages] =
    useState(false);
  const [dontSyncUserDisabledChapters, setDontSyncUserDisabledChapters] =
    useState(true);
  const [originalState, setOriginalState] = useState(null);
  const isInitializedRef = useRef(false);

  const isPfeOnlyPlan = user?.isOccupational === false;

  useEffect(() => {
    getCustomSync();
  }, []);

  useEffect(() => {
    if (isLoadingCustomSyncData || !customSync || isInitializedRef.current) {
      return;
    }

    const resolvedRateScope = isPfeOnlyPlan
      ? 'pfe'
      : customSync.rateScope || 'both';

    setRateScope(resolvedRateScope);
    setFavoriteQuestions(customSync.favoriteQuestions);
    setOnlyPersonalizedStudyChapters(customSync.onlyPersonalizedStudyChapters);
    setOnlyQuestionsWithoutImages(customSync.onlyQuestionsWithoutImages);
    setDontSyncUserDisabledChapters(customSync.dontSyncUserDisabledChapters);

    setOriginalState({
      rateScope: resolvedRateScope,
      favoriteQuestions: customSync.favoriteQuestions,
      onlyPersonalizedStudyChapters: customSync.onlyPersonalizedStudyChapters,
      onlyQuestionsWithoutImages: customSync.onlyQuestionsWithoutImages,
      dontSyncUserDisabledChapters: customSync.dontSyncUserDisabledChapters,
    });
    isInitializedRef.current = true;
  }, [customSync, isLoadingCustomSyncData, isPfeOnlyPlan]);

  useEffect(() => {
    if (isLoadingCustomSyncData || !customSync || !isInitializedRef.current) {
      return;
    }

    setOriginalState({
      rateScope: isPfeOnlyPlan ? 'pfe' : customSync.rateScope || 'both',
      favoriteQuestions: customSync.favoriteQuestions,
      onlyPersonalizedStudyChapters: customSync.onlyPersonalizedStudyChapters,
      onlyQuestionsWithoutImages: customSync.onlyQuestionsWithoutImages,
      dontSyncUserDisabledChapters: customSync.dontSyncUserDisabledChapters,
    });
  }, [customSync]);

  useEffect(() => {
    if (!customSyncMessage || !customSyncMessage.message) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setCustomSyncMessage({
        type: '',
        message: '',
      });
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [customSyncMessage, setCustomSyncMessage]);

  const pendingConfig = useMemo(() => {
    if (!originalState) return null;

    if (onlyPersonalizedStudyChapters) {
      return {
        rateScope,
        favoriteQuestions: false,
        onlyPersonalizedStudyChapters: true,
        onlyQuestionsWithoutImages: false,
        dontSyncUserDisabledChapters: false,
      };
    }

    return {
      rateScope,
      favoriteQuestions,
      onlyPersonalizedStudyChapters,
      onlyQuestionsWithoutImages,
      dontSyncUserDisabledChapters,
    };
  }, [
    originalState,
    rateScope,
    favoriteQuestions,
    onlyPersonalizedStudyChapters,
    onlyQuestionsWithoutImages,
    dontSyncUserDisabledChapters,
  ]);

  const hasChanges = () => {
    if (!originalState || !pendingConfig) return false;

    return (
      pendingConfig.rateScope !== originalState.rateScope ||
      pendingConfig.favoriteQuestions !== originalState.favoriteQuestions ||
      pendingConfig.onlyPersonalizedStudyChapters !==
        originalState.onlyPersonalizedStudyChapters ||
      pendingConfig.onlyQuestionsWithoutImages !==
        originalState.onlyQuestionsWithoutImages ||
      pendingConfig.dontSyncUserDisabledChapters !==
        originalState.dontSyncUserDisabledChapters
    );
  };

  const handleSave = () => {
    if (
      isLoadingCustomSyncData ||
      !customSync ||
      !hasChanges() ||
      !pendingConfig
    )
      return;

    updateCustomSync(pendingConfig);
  };

  const renderDescription = (id, description, tooltip, isTitle = false) => (
    <div className="sync-description-with-tooltip">
      <span className={`sync-option-description ${isTitle ? 'is-title' : ''}`}>
        {description}
      </span>

      <button
        type="button"
        id={`custom-sync-${id}-info`}
        className="sync-info-button"
        aria-label={`${description} details`}
      >
        <i className="material-icons">info_outline</i>
      </button>

      <UncontrolledTooltip placement="top" target={`custom-sync-${id}-info`}>
        {tooltip}
      </UncontrolledTooltip>
    </div>
  );

  const renderToggleRow = (
    id,
    description,
    tooltip,
    value,
    onToggle,
    disabled = false,
  ) => (
    <Row className={`sync-toggle-row ${disabled ? 'disabled' : ''}`}>
      <Col xs={9}>{renderDescription(id, description, tooltip)}</Col>

      <Col xs={3} className="sync-toggle-control">
        <button
          type="button"
          className={`sync-toggle ${value ? 'on' : ''}`}
          onClick={disabled ? undefined : onToggle}
          role="switch"
          aria-checked={value}
          aria-label={description}
          aria-disabled={disabled}
          disabled={disabled}
        >
          <span className="sync-toggle-knob">
            {value && <i className="icon-check2" />}
          </span>
        </button>
      </Col>
    </Row>
  );

  const isPersonalizedStudyExclusive = onlyPersonalizedStudyChapters;
  const allFavoriteQuestionsLabel =
    rateScope === 'skt'
      ? 'Sync All Favorite Questions in MY SKT.'
      : rateScope === 'pfe'
        ? 'Sync All Favorite Questions in MY PFE.'
        : 'Sync All Favorite Questions in MY SKT or PFE.';
  const allFavoriteQuestionsTooltip =
    rateScope === 'skt'
      ? 'All favorite questions in MY SKT will download during mobile sync.'
      : rateScope === 'pfe'
        ? 'All favorite questions in MY PFE will download during mobile sync.'
        : 'All favorite questions in MY SKT or PFE will download during mobile sync.';

  return (
    <div className="custom-sync-component">
      <div className="header">
        <div className="title">Select what to sync with your mobile app</div>
      </div>

      {!isLoadingCustomSyncData && !customSync ? (
        <div className="sync-loading" role="alert">
          Unable to load mobile sync settings. Please refresh and try again.
        </div>
      ) : isLoadingCustomSyncData || !originalState ? (
        <div className="sync-loading" aria-live="polite">
          <CircularLoading className="loading-gif-waiting" />
          <span>Loading mobile sync settings...</span>
        </div>
      ) : (
        <>
          <div className="sync-section rate-scope-section">
            <Row className="rate-scope-row">
              <Col xs={12} sm={7} md={8}>
                {renderDescription(
                  'rate-scope',
                  'AFSC to sync',
                  'Only content for the selected AFSC will download during mobile sync.',
                  true,
                )}
              </Col>

              <Col xs={12} sm={5} md={4} className="rate-scope-control">
                <div
                  className="sync-view-switch rate-scope-switch"
                  role="group"
                  aria-label="AFSC content to sync"
                >
                  {RATE_SCOPES.map((option) => {
                    const isActive = rateScope === option.value;
                    const isLocked = isPfeOnlyPlan && option.value !== 'pfe';

                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`sync-view-btn ${isActive ? 'active' : ''} ${
                          isLocked ? 'locked' : ''
                        }`}
                        onClick={() => {
                          if (isLocked || isActive) return;
                          setRateScope(option.value);
                        }}
                        aria-pressed={isActive}
                        aria-disabled={isLocked}
                        disabled={isLocked}
                      >
                        {option.label}
                        {isLocked && <i className="icon-lock sync-lock-icon" />}
                      </button>
                    );
                  })}
                </div>
              </Col>
            </Row>
          </div>

          {renderToggleRow(
            'personalized-study',
            'Sync only chapters you have added to your Personalized Study list.',
            'All questions that do not belong to any selected chapter in your Personalized Study list will be skipped during mobile sync.',
            onlyPersonalizedStudyChapters,
            () => setOnlyPersonalizedStudyChapters((previous) => !previous),
          )}

          {renderToggleRow(
            'favorites',
            allFavoriteQuestionsLabel,
            allFavoriteQuestionsTooltip,
            isPersonalizedStudyExclusive ? false : favoriteQuestions,
            () => setFavoriteQuestions((previous) => !previous),
            isPersonalizedStudyExclusive,
          )}

          {renderToggleRow(
            'without-images',
            'Sync only questions that do not include images.',
            'All questions with images will be skipped during mobile sync.',
            isPersonalizedStudyExclusive ? false : onlyQuestionsWithoutImages,
            () => setOnlyQuestionsWithoutImages((previous) => !previous),
            isPersonalizedStudyExclusive,
          )}

          {renderToggleRow(
            'disabled-chapters',
            'Skip chapters you have disabled.',
            'All questions in disabled chapters will be skipped during mobile sync.',
            isPersonalizedStudyExclusive ? false : dontSyncUserDisabledChapters,
            () => setDontSyncUserDisabledChapters((previous) => !previous),
            isPersonalizedStudyExclusive,
          )}

          <div className="sync-footer">
            {customSyncMessage && customSyncMessage.message && (
              <span
                className={`message-${customSyncMessage.type}`}
                aria-live="polite"
              >
                {customSyncMessage.message}
              </span>
            )}

            <button
              type="button"
              className="btn btn-primary sync-save-btn"
              disabled={isLoadingCustomSync || !hasChanges()}
              onClick={handleSave}
            >
              {isLoadingCustomSync ? 'Saving...' : 'Save'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default CustomSyncView;
